import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import TeacherandKids from "../../assets/images/teacher-and-kids.png";
import CredentialSchema from "../../app/validations/CredentialFormValidation";
import { CredentialFormData } from "../../app/models/Credentials";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import agent from "../../app/api/agent";
import CredentialConfirmModal from "./CredentialConfirmModal";
import { useState } from "react";

export default function CredentialForm({
  preloadedValues,
}: {
  preloadedValues: CredentialFormData;
}) {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useForm<CredentialFormData>({
    resolver: yupResolver(CredentialSchema),
    defaultValues: preloadedValues,
  });

  const [formData, setFormData] = useState<CredentialFormData | null>(null);

  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleConfirmModal = async (data: CredentialFormData) => {
    await agent.Credentials.UpdateCredentials(data).then(() => {
      navigate("/welcome/How-To-Video");
    });
    setShowModal(false);
  };

  const onSubmit = async (data: CredentialFormData) => {
    setFormData(data);
    setShowModal(true);
  };

  const handleCredential = () => {
    // if(formData?.credentialTypeId === 1){
    // return "Infant-Toddler"}else{
    //   if(formData?.credentialTypeId === 2){
    //   return "Preschool"}
    // }

    switch (formData?.credentialTypeId) {
      case 1:
        return "Center-Based Infant-Toddler";
      case 2:
        return "Center-Based Preschool";
      case 3:
        return "Family Child Care";
      case 4:
        return "Home Visitor";
      default:
        return "";
    }
  };

  return (
    <Box>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container>
          <Grid item md={7}>
            <Typography sx={{ fontWeight: "bold" }}>
              What type of CDA Credential are you aspiring to earn?
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl
                  component="fieldset"
                  className={errors.credentialTypeId ? "error" : ""}
                >
                  <Controller
                    control={control}
                    name="credentialTypeId"
                    render={({ field }) => (
                      <RadioGroup
                        aria-label="CDA Credential"
                        {...field}
                        row
                        className={errors.credentialTypeId ? "error" : ""}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <FormControlLabel
                              value={1}
                              control={<Radio />}
                              labelPlacement="end"
                              label={
                                <Box>
                                  <Typography mt={3}>
                                    Center-Based Infant-Toddler
                                  </Typography>
                                  <Typography>
                                    (working with children ages birth-36 months)
                                  </Typography>
                                </Box>
                              }
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControlLabel
                              value={3}
                              control={<Radio />}
                              labelPlacement="end"
                              label={
                                <Box>
                                  <Typography mt={3}>
                                    Family Child Care
                                  </Typography>
                                  <Typography>
                                    (working with children ages birth-5 years
                                    old)
                                  </Typography>
                                </Box>
                              }
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControlLabel
                              value={2}
                              control={<Radio />}
                              labelPlacement="end"
                              label={
                                <Box>
                                  <Typography mt={3}>
                                    Center-Based Preschool
                                  </Typography>
                                  <Typography>
                                    (working with children ages 3-5 years old)
                                  </Typography>
                                </Box>
                              }
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControlLabel
                              value={4}
                              control={<Radio />}
                              labelPlacement="end"
                              label={
                                <Box>
                                  <Typography mt={3}>Home Visitor</Typography>
                                  <Typography>
                                    (working with children ages birth-5 years
                                    old)
                                  </Typography>
                                </Box>
                              }
                            />
                          </Grid>
                        </Grid>
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <Typography sx={{ fontWeight: "bold" }} mt={4}>
              Are you seeking a language specialization?
            </Typography>
            <Typography mt={2}>
              Language specialization is based on the language(s) you use in
              your daily work with children and families and has
              special/additional requirements. This portfolio tool supports a
              bilingual specialization in English and a second language.
            </Typography>
            <Controller
              control={control}
              name="languageSpecialization"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value} // Set the checked state based on the field value
                      onChange={(e) => field.onChange(e.target.checked)} // Update the field value when the checkbox is changed
                    />
                  }
                  labelPlacement="end"
                  label={
                    <Box>
                      <Typography mt={3}>
                        Add bilingual language specialization
                      </Typography>
                      <Typography>(English and a second language)</Typography>
                    </Box>
                  }
                />
              )}
            />
            <Typography mt={2}>
              If you are seeking a monolingual specialization in a language
              other than English, consult your CDA Competency Standards book or
              contact the Council for Professional Recognition directly for
              further guidance.
            </Typography>
            <LoadingButton
              type="submit"
              loading={isSubmitting}
              disabled={!isValid}
              variant="contained"
              sx={{ textTransform: "none", mb: 3, mt: 4 }}
            >
              Next Step
            </LoadingButton>
          </Grid>
          <Grid item md={5} xs={12} sx={{ textAlign: "left" }}>
            <img
              src={TeacherandKids}
              alt="teacher-and-kids"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </Grid>
        </Grid>
      </Box>
      <CredentialConfirmModal
        isOpen={showModal}
        credMessage={handleCredential()}
        biMessage={
          formData?.languageSpecialization ? "Selected" : "did not select"
        }
        onConfirm={() => handleConfirmModal(formData as CredentialFormData)}
        onCancel={handleCloseModal}
      />
    </Box>
  );
}
